/**
 * Manpower S-Curve — planned vs actual headcount derived from the
 * Fabrication & Construction schedule, filterable by contractor and trade.
 */
import { useEffect, useMemo, useState } from 'react';
import {
  Area, CartesianGrid, ComposedChart, Legend, Line, ResponsiveContainer, Tooltip, XAxis, YAxis,
} from 'recharts';
import { Loader2, Users } from 'lucide-react';
import api from '@/lib/api';
import { usePanelTheme } from '@/components/project/panelTheme';

const SCALES = [['day', 'Daily'], ['week', 'Weekly'], ['month', 'Monthly']];

const ManpowerSCurve = ({ projectId }) => {
  const T = usePanelTheme();
  const [interval, setIntervalScale] = useState('week');
  const [contractor, setContractor] = useState('');
  const [trade, setTrade] = useState('');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!projectId) return;
    let alive = true;
    setLoading(true);
    const qs = new URLSearchParams({ interval });
    if (contractor) qs.set('contractor', contractor);
    if (trade) qs.set('trade', trade);
    api.get(`/v1/projects/${projectId}/overview/manpower-s-curve?${qs.toString()}`)
      .then((r) => { if (alive) setData(r.data); })
      .catch(() => { if (alive) setData(null); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [projectId, interval, contractor, trade]);

  const s = data?.summary || {};
  const series = useMemo(() => (data?.series || []).map((p) => ({
    period: p.period,
    Planned: p.plannedHeadcount,
    Actual: p.actualHeadcount,
    'Cum Planned': p.cumPlannedManDays,
    'Cum Actual': p.cumActualManDays,
  })), [data]);

  const Select = ({ value, onChange, options, placeholder, testId }) => (
    <select value={value} onChange={(e) => onChange(e.target.value)} data-testid={testId}
      className="text-[10px] rounded-lg px-2 py-1 outline-none"
      style={{ background: T.inner, border: `1px solid ${T.border}`, color: T.text }}>
      <option value="">{placeholder}</option>
      {options.map((o) => <option key={o} value={o}>{o}</option>)}
    </select>
  );

  const stats = [
    ['Peak planned', s.peakPlanned ?? 0, T.accent],
    ['Peak actual', s.peakActual ?? 0, T.good],
    ['Planned MD', Math.round(s.plannedManDays ?? 0).toLocaleString('en-IN'), T.violet],
    ['Actual MD', Math.round(s.actualManDays ?? 0).toLocaleString('en-IN'), T.warn],
    ['Utilisation', `${s.utilisationPct ?? 0}%`, (s.utilisationPct ?? 0) >= 90 ? T.good : T.bad],
  ];

  return (
    <div className="rounded-2xl overflow-hidden" data-testid="manpower-scurve-card"
         style={{ background: T.card, border: `1px solid ${T.border}` }}>
      <div className="px-5 pt-4 pb-3 flex flex-wrap items-center gap-2"
           style={{ background: T.sheen, borderBottom: `1px solid ${T.border}` }}>
        <span className="w-7 h-7 rounded-lg grid place-items-center" style={{ background: `${T.accent}1F` }}>
          <Users className="w-3.5 h-3.5" style={{ color: T.accent }} />
        </span>
        <div className="min-w-0">
          <h3 className="text-sm font-semibold leading-tight" style={{ color: T.text }}>
            Manpower S-Curve — Planned vs Actual Headcount
          </h3>
          <p className="text-[10px]" style={{ color: T.muted }}>
            From the F&amp;C schedule · {s.activityCount ?? 0} activities · {series.length} {interval} periods
          </p>
        </div>
        <div className="ml-auto flex items-center gap-1.5 flex-wrap">
          <Select value={contractor} onChange={setContractor} placeholder="All contractors"
                  options={data?.filters?.contractors || []} testId="manpower-filter-contractor" />
          <Select value={trade} onChange={setTrade} placeholder="All trades"
                  options={data?.filters?.trades || []} testId="manpower-filter-trade" />
          <div className="flex items-center gap-1" data-testid="manpower-scale-toggle">
            {SCALES.map(([key, label]) => (
              <button key={key} type="button" onClick={() => setIntervalScale(key)}
                className="text-[10px] px-2 py-0.5 rounded-full font-medium transition-colors"
                style={{ background: interval === key ? T.accent : 'transparent',
                         border: `1px solid ${interval === key ? T.accent : T.border}`,
                         color: interval === key ? '#fff' : T.muted }}
                data-testid={`manpower-scale-${key}`}>{label}</button>
            ))}
          </div>
        </div>
      </div>

      <div className="p-4 space-y-3">
        <div className="grid grid-cols-2 md:grid-cols-5 gap-2" data-testid="manpower-stats">
          {stats.map(([label, value, tone]) => (
            <div key={label} className="rounded-xl px-2.5 py-2"
                 style={{ background: T.inner, border: `1px solid ${T.border}` }}>
              <div className="text-[9px] uppercase tracking-wider" style={{ color: T.muted }}>{label}</div>
              <div className="text-sm font-mono font-semibold" style={{ color: tone }}>{value}</div>
            </div>
          ))}
        </div>

        {loading ? (
          <div className="h-64 flex items-center justify-center gap-2 text-xs" style={{ color: T.muted }}
               data-testid="manpower-loading">
            <Loader2 className="w-4 h-4 animate-spin" /> Building manpower curve…
          </div>
        ) : series.length === 0 ? (
          <div className="h-64 flex items-center justify-center text-xs text-center px-8" style={{ color: T.muted }}
               data-testid="manpower-empty">
            No F&amp;C activities carry man-hours for this filter yet — load the fabrication &amp;
            construction schedule, or clear the contractor / trade filter.
          </div>
        ) : (
          <div className="h-64" data-testid="manpower-chart">
            <ResponsiveContainer width="100%" height="100%">
              <ComposedChart data={series} margin={{ top: 6, right: 8, left: -14, bottom: 0 }}>
                <defs>
                  <linearGradient id="mpPlan" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor={T.accent} stopOpacity={0.28} />
                    <stop offset="95%" stopColor={T.accent} stopOpacity={0} />
                  </linearGradient>
                  <linearGradient id="mpAct" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor={T.good} stopOpacity={0.28} />
                    <stop offset="95%" stopColor={T.good} stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke={T.grid} />
                <XAxis dataKey="period" stroke={T.muted} fontSize={9} angle={-35} textAnchor="end" height={48}
                       interval="preserveStartEnd" minTickGap={interval === 'day' ? 22 : 6} />
                <YAxis yAxisId="left" stroke={T.muted} fontSize={10} />
                <YAxis yAxisId="right" orientation="right" stroke={T.muted} fontSize={10} />
                <Tooltip contentStyle={{ background: T.card, border: `1px solid ${T.border}`, fontSize: 11, color: T.text }} />
                <Legend wrapperStyle={{ fontSize: 10 }} />
                <Area yAxisId="left" type="monotone" dataKey="Planned" stroke={T.accent} fill="url(#mpPlan)" strokeWidth={2} />
                <Area yAxisId="left" type="monotone" dataKey="Actual"  stroke={T.good}   fill="url(#mpAct)"  strokeWidth={2} />
                <Line yAxisId="right" type="monotone" dataKey="Cum Planned" stroke={T.violet} strokeDasharray="5 4" strokeWidth={1.6} dot={false} />
                <Line yAxisId="right" type="monotone" dataKey="Cum Actual"  stroke={T.warn}   strokeWidth={1.6} dot={false} />
              </ComposedChart>
            </ResponsiveContainer>
          </div>
        )}

        {(data?.byTrade || []).length > 0 && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-1">
            {[['Man-days by trade', data.byTrade], ['Man-days by contractor', data.byContractor]].map(([label, rows]) => (
              <div key={label}>
                <div className="text-[10px] uppercase tracking-wider mb-1.5" style={{ color: T.muted }}>{label}</div>
                <div className="space-y-1.5">
                  {(rows || []).slice(0, 5).map((r) => {
                    const max = Math.max(1, ...(rows || []).map((x) => x.manDays));
                    return (
                      <div key={r.name}>
                        <div className="flex justify-between text-[10px] mb-0.5">
                          <span className="truncate pr-2" style={{ color: T.text2 }}>{r.name}</span>
                          <span className="font-mono" style={{ color: T.muted }}>{Math.round(r.manDays).toLocaleString('en-IN')} MD</span>
                        </div>
                        <div className="h-1.5 rounded-full overflow-hidden" style={{ background: T.inner }}>
                          <div className="h-full rounded-full transition-all duration-700"
                               style={{ width: `${(r.manDays / max) * 100}%`, background: T.accent }} />
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default ManpowerSCurve;
